/**
 * MemberDetailPage - Public Member Profile View
 * 
 * Beautiful read-only profile page for viewing other members,
 * with contact options and shared activity insights.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7B
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Button, EmptyState, RoleBadge, ProfileHeaderSkeleton, ListItemSkeleton } from '../components';
import { Skeleton } from '../components/ui/Skeleton';
import { createMemberDetailMobileMenu } from '../components/MobileContextMenu';
import { 
  User, 
  MapPin, 
  Calendar, 
  Trophy, 
  MessageCircle, 
  UserPlus, 
  Shield,
  TrendingUp,
  Users
} from 'lucide-react';
import type { User as UserType } from '../lib/api/profile';

interface MemberDetailResponse {
  member: UserType;
  shared_clubs?: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
  }>;
  stats?: {
    total_events: number;
    clubs_joined: number;
    member_since: string;
  };
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'default' | 'accent' | 'green' | 'blue';
}

function StatCard({ title, value, icon, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'bg-oro-surface-alt text-text-muted',
    accent: 'bg-accent/10 text-accent',
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400'
  };

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <div className="text-xl font-bold text-text-primary">{value}</div>
          <div className="text-sm text-text-muted">{title}</div>
        </div>
      </div>
    </div>
  );
}

export default function MemberDetailPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'about' | 'activity' | 'clubs'>('about');

  // Fetch member details
  const { data: memberData, isLoading, error } = useQuery({
    queryKey: ['member', id],
    queryFn: () => apiGet<MemberDetailResponse>(`/members/${id}`),
    enabled: !!id
  });

  const member = memberData?.member;
  const sharedClubs = memberData?.shared_clubs || [];
  const stats = memberData?.stats;

  // Create mobile menu configuration
  const memberDetailMenu = createMemberDetailMobileMenu(navigate, logout);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Member Profile" onBack={() => navigate(-1)} mobileMenu={memberDetailMenu} />
        <div className="safe-area-top pt-4 px-4 pb-20">
          {/* Profile Header Skeleton */}
          <ProfileHeaderSkeleton />
          
          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-2 gap-4 my-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-oro-surface border border-oro-border rounded-xl p-4">
                <Skeleton className="h-10 w-10 mb-2" />
                <Skeleton className="h-6 w-12 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
          
          {/* Shared Clubs Skeleton */}
          <div className="space-y-4">
            <Skeleton className="h-6 w-32" />
            {[1, 2, 3].map((i) => (
              <ListItemSkeleton key={i} />
            ))}
          </div>
          
          {/* Stats Skeleton */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-oro-surface border border-oro-border rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-oro-surface-alt rounded w-2/3 mb-2"></div>
                <div className="h-6 bg-oro-surface-alt rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Member Profile" onBack={() => navigate(-1)} mobileMenu={memberDetailMenu} />
        <div className="safe-area-top pt-4 px-4 pb-20">
          <div className="py-12">
            <EmptyState
              icon={<User className="w-6 h-6" />}
              title="Member not found"
              message="This member profile could not be found or may have been removed."
              actionLabel="Back"
              onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const displayName = member.public_display_name || member.name || 'Unknown User';
  const memberSince = stats?.member_since ? formatDate(stats.member_since) : 'Recently';

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar title="Member Profile" onBack={() => navigate(-1)} mobileMenu={memberDetailMenu} />
      
      <div className="safe-area-top pt-4 px-4 pb-20">
        {/* Profile Header */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <div className="flex items-start gap-4 mb-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-20 h-20 bg-oro-surface-alt rounded-full flex items-center justify-center overflow-hidden">
                {member.profile_image_url ? (
                  <img 
                    src={member.profile_image_url} 
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-text-muted" />
                )}
              </div>
              {member.user_level === 'premium' && (
                <div className="absolute -bottom-1 -right-1 bg-accent rounded-full p-1">
                  <Shield className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            
            {/* Basic Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-text-primary mb-1 truncate">
                {displayName}
              </h1>
              {member.tagline && (
                <p className="text-text-secondary text-sm mb-2 line-clamp-2">
                  {member.tagline}
                </p>
              )}
              
              <div className="flex items-center gap-4 text-sm text-text-muted">
                {member.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>{member.location}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Member since {memberSince}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Primary Sport */}
          {member.primary_sport && (
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-text-primary">{member.primary_sport}</span>
              {member.secondary_sports && member.secondary_sports.length > 0 && (
                <span className="text-xs text-text-muted">
                  +{member.secondary_sports.length} more
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="primary" size="sm" className="flex-1">
              <MessageCircle className="w-4 h-4 mr-2" />
              Message
            </Button>
            <Button variant="secondary" size="sm" className="flex-1">
              <UserPlus className="w-4 h-4 mr-2" />
              Follow
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <StatCard
              title="Events"
              value={stats.total_events}
              icon={<Calendar className="w-4 h-4" />}
              color="accent"
            />
            <StatCard
              title="Clubs"
              value={stats.clubs_joined}
              icon={<Users className="w-4 h-4" />}
              color="blue"
            />
            <StatCard
              title="Level"
              value={member.user_level === 'premium' ? 'Pro' : 'Free'}
              icon={<TrendingUp className="w-4 h-4" />}
              color={member.user_level === 'premium' ? 'green' : 'default'}
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-1 mb-6">
          <div className="grid grid-cols-3 gap-1">
            {[
              { id: 'about', label: 'About' },
              { id: 'activity', label: 'Activity' },
              { id: 'clubs', label: 'Clubs' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-accent text-black'
                    : 'text-text-muted hover:text-text-secondary hover:bg-oro-surface-alt'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'about' && (
          <div className="space-y-4">
            {/* Sports & Interests */}
            {(member.primary_sport || (member.secondary_sports && member.secondary_sports.length > 0)) && (
              <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
                <h3 className="font-medium text-text-primary mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-accent" />
                  Sports & Interests
                </h3>
                
                <div className="space-y-2">
                  {member.primary_sport && (
                    <div>
                      <span className="text-sm text-text-muted">Primary: </span>
                      <span className="text-sm font-medium text-text-primary">{member.primary_sport}</span>
                    </div>
                  )}
                  
                  {member.secondary_sports && member.secondary_sports.length > 0 && (
                    <div>
                      <span className="text-sm text-text-muted">Also enjoys: </span>
                      <span className="text-sm text-text-secondary">
                        {member.secondary_sports.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Birthday */}
            {(member.birthday_month && member.birthday_day) && (
              <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
                <h3 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-accent" />
                  Birthday
                </h3>
                <p className="text-sm text-text-secondary">
                  {new Date(2024, member.birthday_month - 1, member.birthday_day).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-4">
            <EmptyState
              icon={<TrendingUp className="w-6 h-6 text-text-muted" />}
              title="Private Activity"
              message="This member's recent activity is private."
            />
          </div>
        )}

        {activeTab === 'clubs' && (
          <div className="space-y-4">
            {sharedClubs.length > 0 ? (
              sharedClubs.map((club) => (
                <div 
                  key={club.id}
                  className="bg-oro-surface border border-oro-border rounded-xl p-4 flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-medium text-text-primary">{club.name}</h3>
                    <RoleBadge role={club.role} />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/clubs/${club.slug}`)}
                  >
                    View Club
                  </Button>
                </div>
              ))
            ) : (
              <EmptyState
                icon={<Users className="w-6 h-6 text-text-muted" />}
                title="No Shared Clubs"
                message="You don't share any clubs with this member yet."
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}